import type { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { apiRoutes } from '../../constants/api-routes.constants';
import { POST } from '../../constants/post.constants';
import { prependDraftToCache } from '../../graphql/cache/draftCacheUpdates';
import { ADD_POST_DRAFT_MUTATION } from '../../graphql/resolvers/draftMutations/addPostDraft.gql';
import { GET_DRAFTS_QUERY } from '../../graphql/resolvers/draftQueries/getDrafts.gql';
import { GET_USER_FEED } from '../../graphql/resolvers/feed/feedQueries/getUserFeed';
import { GET_POST_REPLY_DRAFTS_QUERY } from '../../graphql/resolvers/replyToPost/queries/getPostReplyDrafts.gql';
import { REPLY_TO_POST_AS_DRAFT_MUTATION } from '../../graphql/resolvers/replyToPost/replyToPostAsDraft.gql';
import { deleteImageFromS3 } from '../../helpers/deleteImagefromS3';
import { useDrafts } from '../../hooks/useDrafts';
import { createNewBlock } from '../../initial-states/textBlocks.initialState';
import {
  DraftInterfaceState,
  PostReplyDraftsInterfaceState,
} from '../../interfaces/post.interface';
import type {
  BlockObject,
  TextBlock,
} from '../../interfaces/textEditor.interface';
import type { UserState } from '../../interfaces/user.interface';
import { draftSlice } from '../../store/draft.store';
import { postReplyDraftSlice } from '../../store/postReplyDraft.store';
import { postReplyDraftsSlice } from '../../store/postReplyDrafts.store';
import { userSlice } from '../../store/user.store';
import { SimpleDialog } from '../UI/SimpleDialog';
import { RichEditorBody } from './RichEditorBody';
import './text-editor.scss';

/**
 * Interface for the properties used by the TextEditor component.
 *
 * @property {string} userId - A string representing the user identifier.
 * @property {boolean} isModalOpen - A boolean indicating whether the modal is open.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsModalOpen - A function to set the state of the modal open status.
 * @property {Function} refetch - A function to refetch data, accepting optional variables and returning a promise resolving to an Apollo query result.
 */
interface TextEditorProps {
  userId: string;
  username: string;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetch?: (
    variables?: Partial<OperationVariables>,
  ) => Promise<ApolloQueryResult<any>>;
  isEditing?: boolean;
}

/**
 * TextEditor is a React functional component that provides a rich text editing experience
 * within a modal dialog. It allows the user to create, modify, and save various blocks of text,
 * such as paragraphs and headings.
 *
 * @param {object} props - The properties object.
 * @param {string} props.username - The username of the currently logged-in user.
 * @param {string} props.userId - The ID of the currently logged-in user.
 * @param {boolean} props.isModalOpen - Flag to determine whether the text editor modal is open.
 * @param {function} props.setIsModalOpen - Function to set the state for closing or opening the modal.
 * @param {function} props.refetch - Function to refetch posts after a new post is added.
 *
 * @returns {React.ReactElement} A React element presenting a modal text editor.
 */
const TextEditor = (props: TextEditorProps): React.ReactElement => {
  const { userId, username, isModalOpen, setIsModalOpen, refetch } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loggedInUser, user } = useSelector(
    (state: UserState) => state.userStore,
  );
  const {
    isEditingDraft,
    isAddingDraft,
    isAddingPostReplyAsDraft,
    savedImageKey,
    feedPostId,
  } = useSelector((state: DraftInterfaceState) => state.draftStore);
  const { drafts } = useDrafts(userId);
  const { postReplyDrafts } = useSelector(
    (state: PostReplyDraftsInterfaceState) => state.postReplyDraftsStore,
  );
  const { UPDATE_USER } = userSlice.actions;
  const {
    IS_ADDING_POST_REPLY_AS_DRAFT_STORE_ACTION,
    FEED_POST_ID,
    IS_ADDING_DRAFT_STORE_ACTION,
    IS_EDITING_DRAFT_STORE_ACTION,
    ADD_TEXT_BLOCKS,
    CLEAR_IMAGE_KEY,
  } = draftSlice.actions;
  const { ADD_POST_REPLY_DRAFT_STORE_ACTION } = postReplyDraftSlice.actions;
  const { SET_POST_REPLY_DRAFTS_STORE_ACTION } = postReplyDraftsSlice.actions;
  const [blocks, setBlocks] = useState<TextBlock[] | []>([]);
  const fileInputRef = useRef(null);
  const [currentBlockIndex, setCurrentBlockIndex] = useState<number | null>(
    null,
  );
  const [initialBlocks, setInitialBlocks] = useState<TextBlock[]>([]);
  const [menuAnchors, setMenuAnchors] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [headingMenuAnchors, setHeadingMenuAnchors] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  useEffect(() => {
    if (isModalOpen && (!initialBlocks || initialBlocks.length === 0)) {
      setInitialBlocks([createNewBlock('Paragraph')]);
    }
  }, [isModalOpen, initialBlocks]);

  const handleSaveTextBlocks = (payload: BlockObject) => {
    // add conditional logic for addPostDraft and addDraftReply
    if (isAddingPostReplyAsDraft) {
      const PostReplyAsDraftPayload = {
        ...payload,
        parentPostId: feedPostId,
      };
      replyToPostAsDraft({
        variables: { input: PostReplyAsDraftPayload },
      }).then(() => refetch && refetch());
    }
    if (isAddingDraft) {
      addPostDraft({ variables: { input: payload } });
    }
  };

  const getUserFeedVariables: {
    userId: string | undefined;
    cursor: string | null;
    limit: number;
  } = {
    userId: user?.id,
    cursor: null,
    limit: 20,
  };

  const [
    replyToPostAsDraft,
    {
      data: postReplyAsDraftData,
      loading: postReplyAsDraftLoading,
      error: postReplyAsDraftError,
    },
  ] = useMutation(REPLY_TO_POST_AS_DRAFT_MUTATION, {
    refetchQueries: [
      { query: GET_USER_FEED, variables: getUserFeedVariables },
      { query: GET_DRAFTS_QUERY, variables: { userId: userId } },
      { query: GET_POST_REPLY_DRAFTS_QUERY, variables: { userId: userId } },
    ],
    awaitRefetchQueries: true,
    onCompleted: ({ replyToPostAsDraft }) => {
      dispatch(FEED_POST_ID(undefined));
      dispatch(ADD_POST_REPLY_DRAFT_STORE_ACTION(replyToPostAsDraft));
      const updatedWithNewReplyDraft =
        postReplyDrafts && postReplyDrafts?.length
          ? [...postReplyDrafts, replyToPostAsDraft]
          : replyToPostAsDraft;
      dispatch(SET_POST_REPLY_DRAFTS_STORE_ACTION(updatedWithNewReplyDraft));
      const updatedUserPayload = {
        ...user,
        postReplyDrafts: updatedWithNewReplyDraft,
      };
      dispatch(UPDATE_USER(updatedUserPayload));
    },
    onError: (ex) => {
      console.error('replyToPostAsDraft useMutation error =>', ex);
    },
  });

  const [addPostDraft, { data, loading, error }] = useMutation(
    ADD_POST_DRAFT_MUTATION,
    {
      variables: {
        input: {
          textBlocks: [],
          username: '',
          userId: '',
          article: false,
        },
      },
      update(cache, { data }) {
        if (data?.addPostDraft) {
          prependDraftToCache(cache, data.addPostDraft);
        }
      },
      onCompleted: ({ addPostDraft }) => {
        const updatedWithNew =
          drafts && drafts?.length ? [...drafts, addPostDraft] : addPostDraft;
        const updatedUserPayload = {
          ...user,
          drafts: updatedWithNew,
        };
        dispatch(UPDATE_USER(updatedUserPayload));
      },
      onError: (ex) => {
        console.error('useMutation error =>', ex);
      },
    },
  );

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => {
    setMenuAnchors((prev) => ({ ...prev, [blockId]: event.currentTarget }));
  };

  const handleOpenHeadingMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => {
    setHeadingMenuAnchors((prev) => ({
      ...prev,
      [blockId]: event.currentTarget,
    }));
  };

  const handleCloseHeadingMenu = (blockId: string) => {
    setHeadingMenuAnchors((prev) => ({ ...prev, [blockId]: null }));
  };

  const handleHeadingLevelChange = (
    index: number,
    level: number,
    setFieldValue: any,
  ) => {
    setFieldValue(`blocks.${index}.data.level`, level);
  };

  const handleMenuClose = (blockId: string) => {
    setCurrentBlockIndex(null);
    setMenuAnchors((prev) => ({ ...prev, [blockId]: null }));
  };

  useEffect(() => {
    if (blocks.length) {
      dispatch(ADD_TEXT_BLOCKS(blocks));
    }
  }, [blocks, dispatch, ADD_TEXT_BLOCKS]);

  const draftsRoute = `${apiRoutes.USER}/${loggedInUser.username}${apiRoutes.DRAFTS}`;

  const SaveDraftContent = async (values: any) => {
    handleSaveTextBlocks(values);
    setBlocks([]);
    if (isEditingDraft) {
      dispatch(IS_EDITING_DRAFT_STORE_ACTION(false));
    }
    if (isAddingDraft) {
      dispatch(IS_ADDING_DRAFT_STORE_ACTION(false));
    }
    if (isAddingPostReplyAsDraft) {
      dispatch(IS_ADDING_POST_REPLY_AS_DRAFT_STORE_ACTION(false));
    }
    setIsModalOpen(false);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    navigate(draftsRoute);
  };

  const handleClickClose = async () => {
    if (savedImageKey?.length) {
      // TRIL-615: was `.map()` with no await, so every rejection escaped this try/catch as an
      // unhandled promise. Harmless while the old REST call resolved-then-logged; now that
      // deleteImageFromS3 rejects (including immediately for legacy keys) those became invisible
      // unhandled rejections. allSettled: one bad key must not abandon the rest.
      const outcomes = await Promise.allSettled(
        savedImageKey.map((key: string) => deleteImageFromS3(key as string)),
      );
      const failed = outcomes.filter((o) => o.status === 'rejected');
      if (failed.length) {
        // KNOWN GAP, stated rather than implied: this close-editor path is console-only, so the
        // user is not told that images they abandoned may still be publicly accessible. The
        // discard-draft path (deleteAllDraftContent) surfaces a warning toast; this close-editor
        // path has no equivalent. Legacy objects that can never be deleted are TRIL-625.
        console.error(
          `Failed to remove ${failed.length} of ${outcomes.length} images from storage`,
          failed,
        );
      }
    }
    setIsModalOpen(false);
    dispatch(IS_ADDING_DRAFT_STORE_ACTION(false));
    setInitialBlocks([]);
    dispatch(CLEAR_IMAGE_KEY(true));
  };

  const dialogBody = (
    <RichEditorBody
      username={username}
      userId={userId}
      initialBlocks={initialBlocks}
      fileInputRef={fileInputRef}
      menuAnchors={menuAnchors}
      headingMenuAnchors={headingMenuAnchors}
      handleMenuClick={handleMenuClick}
      handleOpenHeadingMenu={handleOpenHeadingMenu}
      handleCloseHeadingMenu={handleCloseHeadingMenu}
      handleHeadingLevelChange={handleHeadingLevelChange}
      handleMenuClose={handleMenuClose}
      handleSave={SaveDraftContent}
    />
  );

  return (
    <div>
      <SimpleDialog
        fullWidth
        actionLabel={POST.SAVE_POST}
        className={'Simple-Dialog-From-Text-Editor'}
        closeModalLabel={POST.CANCEL}
        dialogBody={dialogBody}
        dialogTitle={POST.CREATE_DRAFT}
        maxWidth={'lg'}
        open={isModalOpen}
        textEditor={true}
        onClose={handleClickClose}
      />
    </div>
  );
};

export default TextEditor;
